import { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import { SYSTEM_SUMMARY } from '@ctw/shared/api/fhir/system-urls';
import { MAX_OBJECTS_PER_REQUEST, fqsRequest, fqsRequestAll } from '@ctw/shared/api/fqs/client';
import { useGraphqlFetchAllPatientResources } from '@ctw/shared/api/fqs/helpers';
import {
  type DocumentReferenceWithBasicsGraphqlResponse,
  documentReferencesQuery,
  documentReferencesWithBasicsQuery,
} from '@ctw/shared/api/fqs/queries/documents';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import type { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import { DocumentReferencesListDocument } from '@ctw/shared/types/graphql/__generated__/graphql';
import {
  QUERY_KEY_PATIENT_DOCUMENTS,
  QUERY_KEY_PATIENT_DOCUMENTS_LIST,
  QUERY_KEY_PATIENT_DOCUMENTS_WITH_BASICS,
} from '@ctw/shared/utils/query-keys';
import type { Basic, DocumentReference } from 'fhir/r4';
import type { GraphQLClient } from 'graphql-request';
import { groupBy, isEqual, orderBy, uniqWith } from 'lodash-es';
import { useMemo } from 'react';

// AN IMPORTANT NOTE ON HOW DA GENERATES DOCUMENTS!
// DA creates document references for sections of a CDA (eg. "Assessments") and the full CDA (eg. "Summary of Care").
// These "section documents" are not what a user would consider a "document" that would be rendered in our documents list.
// However, they contain free text content that we do want to display, often as an "encounter note".

interface UsePatientDocumentsListArgs {
  excludeSuspectedDuplicates: boolean;
}

export function usePatientDocumentsList({
  excludeSuspectedDuplicates,
}: UsePatientDocumentsListArgs) {
  const { data, isError, isFetching, isLoading } = useGraphqlFetchAllPatientResources<
    'DocumentReference',
    typeof DocumentReferencesListDocument
  >({
    gcTime: 0,
    staleTime: 0,
    queryId: QUERY_KEY_PATIENT_DOCUMENTS_LIST,
    resourceType: 'DocumentReference',
    query: DocumentReferencesListDocument,
    variables: {
      sort: {
        lastUpdated: 'DESC',
      },
    },
  });

  const allDocuments = useMemo(
    () =>
      (data ?? []).flatMap(
        (response) =>
          (response.DocumentReferenceConnection?.edges ?? []).map(
            (edge) =>
              new DocumentModel(
                // NOTE: This is a PARTIAL DocumentReference, but DocumentModel does not accept that
                // TODO: Switch this once we update DocumentModel types
                edge.node as DocumentReference,
                undefined,
                // NOTE: These are PARTIAL Basics, but DocumentModel does not accept that
                // TODO: Switch this once we update DocumentModel types
                edge.node.BasicList as Array<Basic>,
              ),
          ) ?? [],
      ),
    [data],
  );

  const topLevelDocuments = useMemo(
    () =>
      orderBy(
        filterToRenderableDocuments({ documents: allDocuments, excludeSuspectedDuplicates }),
        'resource.content[0].attachment.creation',
        ['desc'],
      ),
    [allDocuments, excludeSuspectedDuplicates],
  );

  return {
    data: topLevelDocuments,
    topLevelDocuments,
    allDocuments,
    isError,
    isFetching,
    isLoading,
  };
}

export function usePatientDocumentsWithBasics(limit = MAX_OBJECTS_PER_REQUEST, enabled = true) {
  return usePatientQuery({
    gcTime: 0,
    staleTime: 0,
    queryId: QUERY_KEY_PATIENT_DOCUMENTS_WITH_BASICS,
    queryKey: [limit],
    queryFn: async ({ graphqlClient, telemetry, patient }) =>
      getDocumentReferencesWithBasics(telemetry, graphqlClient, patient, [MAX_OBJECTS_PER_REQUEST]),
    enabled,
  });
}

export function usePatientDocuments(limit = MAX_OBJECTS_PER_REQUEST, enabled = true) {
  return usePatientQuery({
    gcTime: 0,
    staleTime: 0,
    queryId: QUERY_KEY_PATIENT_DOCUMENTS,
    queryKey: [limit],
    queryFn: async ({ graphqlClient, telemetry, patient }) =>
      getDocumentReferences(telemetry, graphqlClient, patient, [MAX_OBJECTS_PER_REQUEST]),
    enabled,
  });
}

export function useDocumentReference(upid: string, id: string) {
  return useCtwQuery({
    gcTime: 0,
    staleTime: 0,
    queryId: `${QUERY_KEY_PATIENT_DOCUMENTS}_${id}`,
    queryKey: [upid, id],
    queryFn: async ({ graphqlClient, telemetry }) =>
      getDocumentReferenceById(telemetry, graphqlClient, upid, id),
  });
}

export function useDocumentReferences(upid: string, ids: Array<string>) {
  return useCtwQuery({
    gcTime: 0,
    staleTime: 0,
    enabled: upid !== '' && ids.length > 0,
    queryId: `${QUERY_KEY_PATIENT_DOCUMENTS}_${ids.join(',')}`,
    queryKey: [upid, ids],
    queryFn: async ({ graphqlClient, telemetry }) =>
      getDocumentReferencesById(telemetry, graphqlClient, upid, ids),
  });
}

export async function getDocumentReferences(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
  keys: Array<number> = [],
) {
  const limit = keys[0];
  const { data } = await fqsRequestAll<DocumentReferenceWithBasicsGraphqlResponse>(
    telemetry,
    graphqlClient,
    documentReferencesQuery,
    'DocumentReferenceConnection',
    {
      upid: patient.UPID,
      cursor: '',
      first: limit,
    },
  );
  return data.DocumentReferenceConnection.edges.map(
    (x) => new DocumentModel(x.node, undefined, x.node.BasicList),
  );
}

export async function getDocumentReferenceById(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  upid: string,
  id: string,
) {
  const { data } = await fqsRequest<DocumentReferenceWithBasicsGraphqlResponse>(
    telemetry,
    graphqlClient,
    documentReferencesWithBasicsQuery,
    {
      upid: upid,
      cursor: '',
      first: 1,
      sort: {},
      filter: {
        ids: { anymatch: [id] },
        tag: { nonematch: [SYSTEM_SUMMARY] },
      },
    },
  );

  return data.DocumentReferenceConnection.edges[0].node
    ? new DocumentModel(data.DocumentReferenceConnection.edges[0].node)
    : null;
}

export async function getDocumentReferencesById(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  upid: string,
  ids: Array<string>,
) {
  const { data } = await fqsRequest<DocumentReferenceWithBasicsGraphqlResponse>(
    telemetry,
    graphqlClient,
    documentReferencesWithBasicsQuery,
    {
      upid,
      cursor: '',
      first: 500,
      sort: {},
      filter: {
        ids: { anymatch: ids },
        tag: { nonematch: [SYSTEM_SUMMARY] },
      },
    },
  );

  return data.DocumentReferenceConnection.edges.map((edge) => new DocumentModel(edge.node));
}

export async function getDocumentReferencesWithBasics(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
  keys: Array<number> = [],
) {
  const limit = keys[0];
  const { data } = await fqsRequestAll<DocumentReferenceWithBasicsGraphqlResponse>(
    telemetry,
    graphqlClient,
    documentReferencesWithBasicsQuery,
    'DocumentReferenceConnection',
    {
      upid: patient.UPID,
      cursor: '',
      first: limit,
    },
  );

  return data.DocumentReferenceConnection.edges.map(
    (x) => new DocumentModel(x.node, undefined, x.node.BasicList),
  );
}

export async function getDocumentReferenceWithBasicsById(
  telemetry: ReturnType<typeof useTelemetry>,
  graphqlClient: GraphQLClient,
  patient: PatientModel,
  ids: Array<string> = [],
) {
  const { data } = await fqsRequest<DocumentReferenceWithBasicsGraphqlResponse>(
    telemetry,
    graphqlClient,
    documentReferencesWithBasicsQuery,
    {
      upid: patient.UPID,
      cursor: '',
      first: 500,
      filter: {
        ids: { anymatch: ids },
        tag: { nonematch: [SYSTEM_SUMMARY] },
      },
      sort: {
        lastUpdated: 'DESC',
      },
    },
  );
  return data.DocumentReferenceConnection.edges.map((x) => new DocumentModel(x.node));
}

interface FilterToRenderableDocumentsArgs {
  documents: Array<DocumentModel>;
  excludeSuspectedDuplicates: boolean;
}

export const filterToRenderableDocuments = ({
  documents,
  excludeSuspectedDuplicates,
}: FilterToRenderableDocumentsArgs) => {
  const highestLevelDocs = filterOutSectionDocuments(documents);
  const isHighestLevelDocument = (docRef: DocumentReference) =>
    highestLevelDocs.find((docModel) => docModel.id === docRef.id);

  const documentModels = documents.filter(
    (doc) =>
      doc.isImage ||
      doc.isPdf ||
      (doc.isCDA && isHighestLevelDocument(doc.resource) && !doc.isZusGeneratedReciprocityDocument),
  );

  if (excludeSuspectedDuplicates) {
    return uniqWith(documentModels, (a, b) =>
      isEqual(suspectedDuplicateFields(a), suspectedDuplicateFields(b)),
    );
  }

  return documentModels;
};

const filterOutSectionDocuments = (docs: Array<DocumentModel>) => {
  const highestLevelDocs: Array<DocumentModel> = [];
  const documentGroups = groupBy(docs, (d) => d.binaryId);

  // If multiple documents share the same binaryId, we want to keep the one with highest number of categories
  // as that is currently the most reliable way to determine the "top level" document.
  Object.keys(documentGroups).forEach((binaryId) => {
    if (binaryId) {
      highestLevelDocs.push(orderBy(documentGroups[binaryId], 'category.length', 'desc')[0]);
    } else {
      highestLevelDocs.push(...documentGroups[binaryId]);
    }
  });

  return highestLevelDocs;
};

const suspectedDuplicateFields = (document: DocumentModel) => [
  document.encounterDate,
  document.dateCreated,
  document.custodian,
  document.title,
];
